import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { Asset } from '../../../../types/asset/asset';
import { useMutation } from '@apollo/client';
import Modal from '../../../../VentoryUI/components/common/Modal';
import { t } from '../../../../types/translation/Translator';
import AssetInfoPane, { AssetInfoType } from './Panes/AssetInfoPane';
import { AssetInput } from '../../../../types/asset/asset.input';
import {
  AssetTransactionMutations,
  DeleteAssetResponse,
  DeleteAssetVariables,
  ReassignAssetResponse,
  ReassignAssetVariables,
  ReturnAssetResponse,
  ReturnAssetVariables,
} from '../../../../graphql/assetTransaction.graphql';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import { AssetTransaction } from '../../../../types/asset_transaction/assetTransaction';
import { AssetMutations, UpdateAssetResponse, UpdateAssetVariables } from '../../../../graphql/asset.graphql';
import UpdateButton from '../../../components/Button/templates/UpdateButton';
import { useRequest } from '../../../../requests/common/requestHook';
import { AssetTransactionRequest } from '../../../../requests/assetTransaction.request';
import { Button, CancelButton, DeleteButton } from '../../../components/Button';

interface ReturnAssetModalInputProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  asset?: Asset;
}

export default function ReturnAssetModal({ open, setOpen, asset }: ReturnAssetModalInputProps) {
  if (!asset) return null;

  const { assetTransactions, setAssetTransactions } = useContext(AssetTransactionContext);

  const [input, setInput] = useState<AssetInput>(
    new AssetInput({ ...asset, product: { ...asset.product, quantity: 1, unitQuantity: '1' } }),
  );
  const [error, setError] = useState<string>('');

  const availableQuantity = useMemo(() => {
    return asset.product.quantity;
  }, [asset]);

  const disabled = useMemo(() => {
    return !input.validate() || !input.location || availableQuantity < input.product.quantity;
  }, [input]);

  const [returnAsset, { loading }] = useMutation<ReturnAssetResponse, ReturnAssetVariables>(
    AssetTransactionMutations.returnAsset,
    {
      onCompleted: res => {
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateAssetResponse, UpdateAssetVariables>(
    AssetMutations.update,
    {
      onCompleted: res => {
        const transactions = res.updateAsset;
        for (const transaction of transactions) {
          assetTransactions.set(transaction.id, new AssetTransaction(transaction));
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [remove, { loading: removeLoading }] = useMutation<DeleteAssetResponse, DeleteAssetVariables>(
    AssetTransactionMutations.remove,
    {
      onCompleted: res => {
        const transactions = res.deleteAsset;
        for (const transaction of transactions) {
          assetTransactions.delete(transaction.id);
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [reassign, { loading: reassignLoading }] = useMutation<ReassignAssetResponse, ReassignAssetVariables>(
    AssetTransactionMutations.reassign,
    {
      onCompleted: res => {
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const [verify, { loading: verifyLoading }] = useRequest(AssetTransactionRequest.verify);

  const handleVerify = async () => {
    const result = await verify({ assetId: input.id });
    if (!result) return;

    const transaction = result[0];
    assetTransactions.set(transaction.id, new AssetTransaction(transaction));
    setAssetTransactions(new Map(assetTransactions));
    setOpen(false);
  };

  const handleReassign = async () => {
    try {
      await reassign({
        variables: {
          asset: input.forUpdate(),
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleReturn = async () => {
    if (asset.location?.entityId !== input.location?.entityId) {
      return handleReassign();
    }

    try {
      await returnAsset({
        variables: {
          asset: input.forUpdate(),
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleRemove = async () => {
    try {
      await remove({
        variables: {
          asset: input.forUpdate(),
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleUpdate = async () => {
    try {
      await update({
        variables: {
          assets: [input.forUpdate()],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleClose = () => {
    setInput(new AssetInput({ ...asset, product: { ...asset.product, quantity: 1, unitQuantity: '1' } }));
    setOpen(false);
    setError('');
  };

  const footer = () => (
    <div className='flex justify-between'>
      <div className='flex gap-1'>
        <UpdateButton
          onClick={handleUpdate}
          loading={updateLoading}
          disabled={loading || reassignLoading || disabled || removeLoading || verifyLoading}
        />
        <Button
          loading={verifyLoading}
          disabled={loading || reassignLoading || disabled || reassignLoading || disabled}
          text={t(TranslationKey.assetTransactionVerifyOwnershipButton).singular.label}
          onClick={handleVerify}
        />
        {!input.location ? (
          <DeleteButton
            disabled={!input.product.quantity}
            loading={removeLoading}
            text={t(TranslationKey.removeFromAssets).singular.label}
            onClick={handleRemove}
          />
        ) : null}
      </div>
      <div className='flex gap-1'>
        <CancelButton disabled={loading || reassignLoading || removeLoading || verifyLoading} onClick={handleClose} />
        <Button
          text={
            asset.location?.entityId === input.location?.entityId
              ? t(TranslationKey.issueReturn).singular.label
              : t(TranslationKey.reassign).singular.label
          }
          loading={loading || reassignLoading}
          disabled={disabled || removeLoading || updateLoading || verifyLoading}
          onClick={handleReturn}
        />
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      error={error}
      onClose={handleClose}
      title={
        asset.location?.entityId === input.location?.entityId
          ? t(TranslationKey.returnAsset).singular.label
          : t(TranslationKey.reassignAsset).singular.label
      }
    >
      <AssetInfoPane
        input={input}
        setInput={setInput}
        footer={footer}
        type={AssetInfoType.return}
        availableQuantity={availableQuantity}
      />
    </Modal>
  );
}
